import React, { useState, useEffect } from "react";
import "../styles/Users.scss";
import {
  changeAccountStatus,
  getAllUsers,
  deleteUser,
  getUsersByRoleId,
} from "../services/api";
import { useNavigate, Link } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { FaEdit, FaTrash } from "react-icons/fa";
import ExcelJS from "exceljs";

const ITEMS_PER_PAGE = 10;

const userTypeOptions = [
  { id: 3, value: "Hiring Manager", label: "Hiring Manager" },
  { id: 4, value: "Recruiter", label: "Recruiter" },
];

// Helper to format date as dd-mm-yyyy
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

function Users() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUserTypes, setSelectedUserTypes] = useState([]);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  // Fetch users from API
  const fetchData = async () => {
    Swal.fire({
      html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
      showConfirmButton: false,
      heightAuto: false,
      customClass: { popup: "loader-swal" },
      allowOutsideClick: false,
    });

    try {
      const apiData = await getAllUsers();
      Swal.close();
      // Check success response
      if (
        (apiData.status === "S" || apiData.result_code === 200) &&
        Array.isArray(apiData.result_info)
      ) {
        const mappedUsers = apiData.result_info.map((u) => ({
          ...u,
          userType: u.role_id || "-", // Map role_id to userType
          dateOfJoining: u.doj || u.dateOfJoining || "", // Flexible date field
          isActive: (() => {
            const val =
              u.is_active ?? u.activation_status ?? u.status ?? u.isActive;
            if (typeof val === "boolean") return val;
            return true;
          })(),
        }));

        setUsers(mappedUsers);
      } else {
        setUsers([]);
      }

      // Handle token errors
      if (
        apiData.status === "F" &&
        [
          "Unauthorized - Missing token",
          "Unauthorized - Invalid token",
        ].includes(apiData.message)
      ) {
        Swal.fire({
          text: "Please login with your credentials",
          icon: "warning",
          showConfirmButton: false,
          timer: 3000,
        }).then(() => {
          localStorage.clear();
          navigate("/login");
        });
      }
    } catch (error) {
      Swal.close();
      console.error("Error fetching users:", error);
      Swal.fire({
        text: "Failed to fetch users. Please try again.",
        icon: "error",
        showConfirmButton: false,
        timer: 3000,
      });
      setUsers([]);
    }
  };
  // Fetch users on component mount
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [navigate]);

  // Handle role-based filtering
  const handleRoleFilter = async (selectedOption) => {
    setSelectedUserTypes(selectedOption);
    console.log("Selected role:", selectedOption);

    if (!selectedOption) {
      fetchData();
      return;
    }

    try {
      Swal.fire({
        html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });

      const apiData = await getUsersByRoleId(selectedOption.id);
      Swal.close();

      if (
        (apiData.status === "S" || apiData.result_code === 200) &&
        Array.isArray(apiData.result_info)
      ) {
        const mappedUsers = apiData.result_info.map((u) => ({
          ...u,
          userType: u.role_id || "-",
          dateOfJoining: u.doj || u.dateOfJoining || "",
          isActive: (() => {
            const val =
              u.is_active ?? u.activation_status ?? u.status ?? u.isActive;
            if (typeof val === "boolean") return val;
            return true;
          })(),
        }));
        setUsers(mappedUsers);
      } else {
        setUsers([]);
      }
    } catch (error) {
      Swal.close();
      console.error("Error fetching users by role:", error);
      Swal.fire({
        text: "Failed to fetch users by role. Please try again.",
        icon: "error",
        showConfirmButton: false,
        timer: 3000,
      });
      setUsers([]);
    }
  };

  // Handle account status change
  const statusChange = async (user) => {
    try {
      const newStatus = user.isActive ? "N" : "Y";
      Swal.fire({
        html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });
      const apiRes = await changeAccountStatus({
        id: user.id,
        is_active: newStatus,
      });
      Swal.close();
      if (
        apiRes.result_code === 200 ||
        apiRes.status === "S" ||
        apiRes.success === true
      ) {
        Swal.fire({
          text: `Account ${
            newStatus === "Y" ? "activated" : "deactivated"
          } successfully`,
          icon: "success",
          showConfirmButton: false,
          timer: 2000,
        }).then(() => {
          fetchData();
        });
      } else {
        const errorMessage =
          apiRes.message || apiRes.error || "Failed to update account status";
        Swal.fire({
          text: errorMessage,
          icon: "error",
          showConfirmButton: true,
        });
      }
    } catch (error) {
      Swal.close();
      Swal.fire({
        text: "Network error. Please try again.",
        icon: "error",
        showConfirmButton: true,
      });
    }
  };

  // Handle user deletion
  const handleDelete = async (id) => {
    const user = users.find((u) => u.id === id);
    Swal.fire({
      title: `Delete user?`,
      text: `Are you sure you want to delete ${user?.name || "this user"}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteUser(id);
          setUsers(users.filter((u) => u.id !== id));
          Swal.fire("Deleted!", "User has been deleted.", "success");
          fetchData();
        } catch (err) {
          Swal.fire("Error", "Failed to delete user.", "error");
        }
      }
    });
  };

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    const matchesText =
      (u.name && u.name.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q));
    return matchesText;
  });

  const pageCount = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handleEdit = (id) => {
    navigate(`/users/edit/${id}`);
  };

  // Reset to page 1 on filter change
  useEffect(() => {
    setPage(1);
  }, [search, selectedUserTypes, users.length]);


   const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Users");

    // Add header row
    worksheet.columns = [
      { header: "Name", key: "name" },
      { header: "Email", key: "email" },
      { header: "Mobile", key: "mobile" },
      { header: "User Type", key: "user_type" },
      { header: "Date of Joining", key: "doj" },
    ];

    // Add data rows
    users.forEach((item) => {
      worksheet.addRow({
        name: item.name || "-",
        email: item.email || "-",
        mobile: item.mobile || "-",
        user_type:
          item.userType === 4
            ? "Recruiter"
            : "Hiring Manager",
        doj: formatDate(item.dateOfJoining) || "-"
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Trigger download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Users.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  function getPageNumbers(currentPage, totalPages, maxPagesToShow = 5) {
    const pages = [];

    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(currentPage - half, 1);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow + 1, 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  return (
    <div className="requisitions-page">
      <div className="requisitions-header-row">
        <div className="requisitions-title-group">
          <h1>Users</h1>
        </div>
        
        <button className="export-btn" onClick={handleExport}>
          Export
        </button>
        
      </div>
      {/* <div className="d-flex align-items-center">
        <div
        style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1.5rem",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder="Search by Name and Email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            minWidth: 260,
            maxWidth: 300,
            padding: "0.4rem .5rem",
            border: "1.5px solid #d1d5db",
            borderRadius: 6,
            background: "#fff",
            fontSize: "1rem",
          }}
          className="custom-placeholder"
                    
        />
        <Select
          options={userTypeOptions}
          value={selectedUserTypes}
          onChange={handleRoleFilter}
          isSearchable={true}
          classNamePrefix="react-select"
          placeholder="Filter by user type"
          styles={{
            control: (base) => ({
              ...base,
              minWidth: 220,
              borderRadius: 6,
              fontSize: "1rem",
              background: "#fff",
              border: "1.5px solid #d1d5db",
            }),
            menu: (base) => ({ ...base, zIndex: 20 }),
          }}
          isClearable={true}
          isMulti={false}
        />

      </div>
              <div className="d-flex justify-content-end">
              <Link to="/users/create" className="create-btn">
                Create User
              </Link>
      
      </div>
      </div> */}
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
  <div
    style={{
      display: "flex",
      gap: "1rem",
      flexWrap: "wrap",
      alignItems: "center",
    }}
  >
    <input
      type="text"
      placeholder="Search by Name and Email"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      style={{
        minWidth: 260,
        maxWidth: 300,
        padding: "0.4rem 0.5rem",
        border: "1.5px solid #d1d5db",
        borderRadius: 6,
        background: "#fff",
        fontSize: "1rem",
      }}
      className="custom-placeholder"
       // ✅ Optional limit for long searches
    />

    <Select
      options={userTypeOptions}
      value={selectedUserTypes}
      onChange={handleRoleFilter}
      isSearchable={true}
      classNamePrefix="react-select"
      placeholder="Filter by user type"
      styles={{
        control: (base) => ({
          ...base,
          minWidth: 220,
          borderRadius: 6,
          fontSize: "1rem",
          background: "#fff",
          border: "1.5px solid #d1d5db",
        }),
        menu: (base) => ({ ...base, zIndex: 20 }),
      }}
      isClearable={true}
      isMulti={false}
    />
  </div>

  <div>
    <Link to="/users/create" className="create-btn">
      Create User
    </Link>
  </div>
</div>

      <div className="users-table-wrapper">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>User Type</th>
              <th>Date of Joining</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  No users found.
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.mobile}</td>
                  <td>
                    {user.userType === 4
                      ? "Recruiter"
                      : user.userType === 3
                      ? "Hiring Manager"
                      : "-"}
                  </td>
                  <td>{formatDate(user.dateOfJoining)}</td>
                  <td>
                    <div className="d-flex align-items-center">
                    <button
                      className="icon-btn"
                      onClick={() => handleEdit(user.id)}
                      title="Edit"
                      style={{
                        color: "#0078b9",
                        background: "none",
                        border: "none",
                        marginRight: 8,
                      }}
                    >
                      <FaEdit size={20} />
                    </button>
                    {/* <button
                      className="icon-btn"
                      onClick={() => statusChange(user)}
                      title={user.isActive ? 'Deactivate' : 'Activate'}
                      style={{
                        color: user.isActive ? '#22c55e' : '#d97706',
                        background: 'none',
                        border: 'none',
                        marginRight: 8,
                      }}
                    >
                      {user.isActive ? (
                        <FaToggleOn size={22} />
                      ) : (
                        <FaToggleOff size={22} />
                      )}
                    </button> */}
                    <button
                      className="icon-btn"
                      onClick={() => handleDelete(user.id)}
                      title="Delete"
                      style={{
                        color: "#e53e3e",
                        background: "none",
                        border: "none",
                      }}
                    >
                      <FaTrash size={20} />
                    </button>
                    </div>
                    
                    
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      {pageCount > 1 && (
  <div className="settings-pagination">
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page - 1)}
      disabled={page === 1}
    >
      &lt;
    </button>
    {getPageNumbers(page, pageCount, 5).map((pageNum) => (
      <button
        key={pageNum}
        className={"pagination-btn" + (page === pageNum ? " active" : "")}
        onClick={() => handlePageChange(pageNum)}
      >
        {pageNum}
      </button>
    ))}
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page + 1)}
      disabled={page === pageCount}
    >
      &gt;
    </button>
  </div>
)}
    </div>
  );
}

export default Users;

